"use client"

import { BrowserRouter } from "react-router-dom"
import { Provider } from "react-redux"
import { useEffect, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { store } from "./store/slices/index.js"
import { validateTokenAndHydrate, logout } from "./store/slices/authSlice.js"
import { clearAIPlannerSession } from "./store/slices/aiPlannerSlice.js"
import { fetchUserProfile } from "./store/slices/userSlice.js"
import { fetchSocialNetworkTypes } from "./store/slices/socialNetworkTypesSlice.js"
import { setTokenProvider, setLogoutHandler } from "./lib/request.js"
import AppRoutes from "./routes/index.jsx"
import { InboxNotificationProvider } from "./contexts/InboxNotificationContext"
import "./App.css"

function AppContent() {
  const dispatch = useDispatch()
  const token = useSelector((state) => state.auth?.token)
  const userProfile = useSelector((state) => state.user?.profile)
  const userStatus = useSelector((state) => state.user?.status)
  const authInitialized = useRef(false)
  const userProfileFetched = useRef(false)
  const hasToken = Boolean(token && String(token).length > 10)

  setTokenProvider(() => token)

  useEffect(() => {
    setTokenProvider(() => token)
    setLogoutHandler(() => {
      dispatch(logout())
    })
  }, [token, dispatch])

  useEffect(() => {
    if (!hasToken || userProfile || userStatus === "loading" || userStatus === "failed") return
    if (userStatus === "idle" && !userProfileFetched.current) {
      userProfileFetched.current = true
      dispatch(fetchUserProfile())
    }
  }, [hasToken, userProfile, userStatus, dispatch])

  useEffect(() => {
    if (!hasToken || userStatus === "failed") return
    dispatch(fetchSocialNetworkTypes())
  }, [hasToken, userStatus, dispatch])

  useEffect(() => {
    if (!token) {
      userProfileFetched.current = false
      dispatch(clearAIPlannerSession())
    }
  }, [token, dispatch])

  useEffect(() => {
    if (authInitialized.current) {
      return
    }
    authInitialized.current = true
    dispatch(validateTokenAndHydrate())
  }, [dispatch])

  return (
    <BrowserRouter
      future={{
        v7_relativeSplatPath: true,
      }}
    >
      <InboxNotificationProvider>
        <AppRoutes />
      </InboxNotificationProvider>
    </BrowserRouter>
  )
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  )
}

export default App
